import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  loginRequest,
  meRequest,
  logoutRequest,
  isApiConfigured,
} from "../lib/authApi";
import {
  saveSession,
  clearSession,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  setSessionActive,
  hasStoredCredentials,
} from "../lib/authStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const bootstrap = useCallback(async () => {
    if (!isApiConfigured() || !hasStoredCredentials()) {
      clearSession();
      setUser(null);
      setIsBootstrapping(false);
      return;
    }

    try {
      const profile = await meRequest(getAccessToken());
      if (profile.isActive === false) {
        throw new Error("Akun dinonaktifkan");
      }
      saveSession({
        accessToken: getAccessToken(),
        refreshToken: getRefreshToken(),
        user: profile,
      });
      setUser(profile);
      setSessionActive(true);
    } catch {
      clearSession();
      setUser(null);
      setSessionActive(false);
    } finally {
      setIsBootstrapping(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    const data = await loginRequest(email, password);
    if (data.user?.isActive === false) {
      throw new Error("Akun dinonaktifkan");
    }
    saveSession({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
    });
    setUser(data.user);
    setSessionActive(true);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    const token = getAccessToken();
    if (token) {
      try {
        await logoutRequest(token);
      } catch {
        // stateless logout — clear client regardless
      }
    }
    clearSession();
    setUser(null);
    setSessionActive(false);
  }, []);

  const ensureValidSession = useCallback(async () => {
    if (!hasStoredCredentials()) return false;
    try {
      const profile = await meRequest(getAccessToken());
      if (profile.isActive === false) throw new Error("inactive");
      saveSession({
        accessToken: getAccessToken(),
        refreshToken: getRefreshToken(),
        user: profile,
      });
      setUser(profile);
      setSessionActive(true);
      return true;
    } catch {
      clearSession();
      setUser(null);
      setSessionActive(false);
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user && getAccessToken()),
      isBootstrapping,
      login,
      logout,
      ensureValidSession,
    }),
    [user, isBootstrapping, login, logout, ensureValidSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useAuthOptionalUser() {
  return getStoredUser();
}
